import { Injectable } from "@angular/core";
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from "@angular/common/http";
import { Observable, throwError } from "rxjs";
import { catchError, switchMap, tap } from "rxjs/operators";
import jwt_decode from 'jwt-decode';
import * as moment from "moment";
import * as pubsub from "pubsub-js";
import { Router } from "@angular/router";
import { Location } from "@angular/common";

import { AuthService } from "../services/auth/auth.service";
import { GeolocationService } from "../services/geolocation/geolocation.service";
import { environment } from "../../../environments/environment";
import { StorageKeysEnum } from "../storage/storage-keys.enum";
import { PubsubEnum } from "../enums/pubsub.enum";

@Injectable()
export class RequestInterceptor implements HttpInterceptor {
    private gettingRefreshToken = false;
    private ckNameToExclude = 'w-bancamonex-uat';
    constructor(
        private authService: AuthService,
        private geolocationService: GeolocationService,
        private router: Router,
        private location: Location
    ) {}
    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        //this.deleteCookiesContaining(this.ckNameToExclude)
        if (this.shouldInterceptRequest(req)) {
            return this.geolocationService.hasLocationPermission().pipe(
                switchMap((hasPermission) => this.handlePermissionAndToken(hasPermission, req, next))
            );
        } else {
            return this.handleNext(req, next);
        }
    }

    private deleteCookie(name: string) {
        document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 UTC;`;
    }
    private deleteCookiesContaining(substring: string) {
        document.cookie.split(';').forEach(cookie => {
            const cookieName = cookie.split('=')[0].trim();
            if (cookieName.includes(substring)) {
                this.deleteCookie(cookieName);
            }
        });
    }

    private shouldInterceptRequest(req: HttpRequest<any>): boolean {
        return (req.url.includes(environment.apiTargetUrl) ||
                req.url.includes('contabilidad-proc') ||
                req.url.includes('/api/')) &&
            !req.url.includes('tokens/oauth/refresh') &&
            !req.url.includes('tokens/oauth/login') &&
            !!sessionStorage.getItem(StorageKeysEnum.ACCESS_TOKEN) &&
            !!sessionStorage.getItem(StorageKeysEnum.REFRESH_TOKEN);
    }

    private handlePermissionAndToken(hasPermission: boolean, req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        this.checkLocationPermission(hasPermission);
        if (this.isNearExpireAccessToken() && !this.gettingRefreshToken) {
            return this.refreshToken(req, next);
        } else {
            req = this.addWsoHeaders(req);
            return this.handleNext(req, next);
        }
    }

    private checkLocationPermission(hasPermission: boolean): void {
        const urlRoute = this.location.path(false);
        if (!hasPermission && urlRoute.includes('nueva-banca-digital/pagos')) {
            console.error("Location permission denied. Handling accordingly...");
            pubsub.publish(PubsubEnum.GEOLOCATION_NOT_ALLOWED_EVENT);
        }
    }

    private refreshToken(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        this.gettingRefreshToken = true;
        return this.authService.refreshToken(this.getHeaders()).pipe(
            switchMap((tokens) => {
                this.updateSessionStorage(tokens);
                this.gettingRefreshToken = false;
                req = this.addWsoHeaders(req);
                return next.handle(req);
            }),
            catchError((error) => {
                this.gettingRefreshToken = false;
                if (error.status === 401) {
                    this.authService.deleteSession();
                }
                return throwError(() => error);
            })
        );
    }

    private updateSessionStorage(tokens: any): void {
        sessionStorage.setItem(StorageKeysEnum.ACCESS_TOKEN, tokens.jwt);
        sessionStorage.setItem(StorageKeysEnum.REFRESH_TOKEN, tokens.refreshToken);
        sessionStorage.setItem(StorageKeysEnum.ACCESS_TOKEN_WSO, tokens.wsoToken);
    }

    private handleNext(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return next.handle(req).pipe(
            tap((event) => {
                if (event instanceof HttpResponse && event.url?.includes("/api/")) {
                    this.updateWsoToken(event);
                }
            })
        );
    }

    private updateWsoToken(event: HttpResponse<any>): void {
        let wsoToken = event.body?.authorizationBanca || event.headers.get("authorizationBanca");
        if (wsoToken) {
            sessionStorage.setItem(StorageKeysEnum.ACCESS_TOKEN_WSO, wsoToken);
            sessionStorage.setItem("JWT_WSO_DECODE", JSON.stringify(jwt_decode(wsoToken)));
        }
    }

    private addWsoHeaders(req: HttpRequest<any>): HttpRequest<any> {
        const controlConsulta = req.url.includes('contabilidad-proc');
        return req.clone({
            setHeaders: this.getHeaders(controlConsulta)
        });
    }

    private getHeaders(controlConsulta?: boolean): any {
        return this.authService.getHeaders(controlConsulta);
    }

    private isNearExpireAccessToken(): boolean {
        const accessToken = sessionStorage.getItem(StorageKeysEnum.ACCESS_TOKEN);
        if (!accessToken) return true;

        const tokenDecode: any = jwt_decode(accessToken);
        const expMoment = moment(tokenDecode.exp * 1000);
        const now = moment();
        const minutesDifference = moment.duration(expMoment.diff(now));
        return minutesDifference.asMinutes() <= 2;
    }

    private handleError(error: any, req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        console.error("Error in interceptor:", error);
        req = this.addWsoHeaders(req);
        return this.handleNext(req, next);
    }
}
